import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grow, Popover, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import * as df from 'date-fns';
import { enUS, de, nl } from 'date-fns/locale';

import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

import {
  KeyboardArrowRightRounded,
  KeyboardArrowLeftRounded,
  CloseRounded,
} from '@mui/icons-material/';
import CalendarIcon from '@/shared/assets/icons/sidenav/session.svg?react';

import { classNames, getMonthList } from '../../../utils';

import SelectUI from '../Select/Select';
import { ButtonUi } from '../Button/Button';
import { TextFieldUi } from '../TextField/TextField';
import { dateRangePickerStyles } from './style';

const defaultValue = [
  {
    startDate: null,
    endDate: new Date(1),
    key: 'selection',
  },
];

export const DateRangePickerUi = (props) => {
  const { t, i18n } = useTranslation();
  const {
    startDatePlaceholder = t('forms.start_date'),
    endDatePlaceholder = 'End Date',
    maxDate = new Date(),
    months = 2,
    direction = 'horizontal',
    preventSnapRefocus = true,
    calendarFocus = 'backwards', // forwards | backwards
    definedRanges = [
      {
        name: 'Week',
        startDate: df.subDays(new Date(), 7),
        endDate: new Date(),
        key: 'selection',
      },
      {
        name: '2 Weeks',
        startDate: df.subDays(new Date(), 14),
        endDate: new Date(),
        key: 'selection',
      },
      {
        name: 'Month',
        startDate: df.subMonths(new Date(), 1),
        endDate: new Date(),
        key: 'selection',
      },
    ],
    value = defaultValue,
    onChange,
    className,
    hidenPlaceholdersOnSelectedValue = false,
  } = props;

  const { dateRangePicker, pickerPopover } = dateRangePickerStyles;

  const [dateValue, setDateValue] = useState(value);
  const [prevDateValue, setPrevDateValue] = useState(value);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const anchorElRef = useRef(null);

  const hidePlaceholder =
    hidenPlaceholdersOnSelectedValue && dateValue[0]?.startDate;

  const formatDate = (date) => df.format(date, 'yyyy-MM-dd');

  const onOpenDateRangePicker = () => {
    setIsDatePickerOpen(true);
  };

  const onResetRangePicker = () => {
    setDateValue(defaultValue);
  };

  const onCloseDateRangePicker = () => {
    setDateValue(prevDateValue || defaultValue);
    setIsDatePickerOpen(false);
  };

  const onSelectRange = (ranges) => setDateValue([ranges.selection]);

  const onSaveDateRange = () => {
    setPrevDateValue(dateValue);
    onChange({
      startDate: formatDate(dateValue?.[0]?.startDate),
      endDate: formatDate(dateValue?.[0]?.endDate),
    });
    setIsDatePickerOpen(false);
  };

  /**
   * Sets date picker text locale
   */
  const localeText = useMemo(() => {
    switch (i18n.language) {
      case 'en':
        return enUS;
      case 'de':
        return de;
      case 'nl':
        return nl;
    }
  }, [i18n]);

  const customDayContent = (day) => {
    const tomorrowDate = df.addDays(new Date(), 1);
    const isTomorrowDate = df.isSameDay(day, tomorrowDate);
    return (
      <>
        {isTomorrowDate && <div className='first-disabled-date' />}
        <span>{df.format(day, 'd')}</span>
      </>
    );
  };
  const renderMonthAndYear = useCallback(
    (focusedDate, changeShownDate, props) => {
      const {
        showMonthArrow,
        locale,
        minDate,
        maxDate,
        showMonthAndYearPickers,
      } = props;
      const upperYearLimit = maxDate.getFullYear();
      const lowerYearLimit = minDate.getFullYear();
      const nextMonthDisabled =
        maxDate.getMonth() === focusedDate.getMonth() &&
        upperYearLimit === focusedDate.getFullYear();

      const monthOptions = getMonthList(locale).map((month) => ({
        text: month,
        value: month,
      }));
      const yearOptions = new Array(upperYearLimit - lowerYearLimit + 1)
        .fill(upperYearLimit)
        .map((val, i) => {
          const year = val - i;
          return {
            text: year,
            value: year,
          };
        });

      return (
        <Box
          onMouseUp={(e) => e.stopPropagation()}
          className='rdrMonthAndYearWrapper'
        >
          {showMonthArrow ? (
            <ButtonUi
              isIconBtn
              className='month-navigation prev-month'
              size='small'
              onClickHandler={() => changeShownDate(-1, 'monthOffset')}
            >
              <KeyboardArrowLeftRounded />
            </ButtonUi>
          ) : null}
          {showMonthAndYearPickers ? (
            <Box className='rdrMonthAndYearPickers'>
              <SelectUI
                className='monthPicker'
                idPrefix='month-picker'
                size='medium'
                name='month-picker'
                selectValue={monthOptions[focusedDate.getMonth()]?.value}
                onChangeHandler={(e) => {
                  const selectedMonth = monthOptions.findIndex(
                    (month) => month.value === e.value,
                  );

                  changeShownDate(selectedMonth, 'setMonth');
                }}
                options={monthOptions}
                hideHints
                autoWidth
              />
              <SelectUI
                className='yearPicker'
                idPrefix='year-picker'
                size='medium'
                name='year-picker'
                selectValue={focusedDate.getFullYear()}
                onChangeHandler={(e) => changeShownDate(e.value, 'setYear')}
                options={yearOptions}
                hideHints
                autoWidth
              />
            </Box>
          ) : (
            <Typography className='monthAndYearPickers' variant='caption'>
              {monthOptions[focusedDate.getMonth()]?.value}{' '}
              {focusedDate.getFullYear()}
            </Typography>
          )}
          {showMonthArrow ? (
            <ButtonUi
              isIconBtn
              className='month-navigation next-month'
              size='small'
              onClickHandler={() => changeShownDate(+1, 'monthOffset')}
              disabled={nextMonthDisabled}
            >
              <KeyboardArrowRightRounded />
            </ButtonUi>
          ) : null}
        </Box>
      );
    },
    [],
  );

  /**
   *  Whether calendar focus month should be forward-driven or backwards-driven
   *  forward-driven - when selected date is more then month from today
   */
  const calendarFocusWay = useMemo(
    () =>
      dateValue?.[0]?.startDate
        ? df.format(dateValue?.[0]?.startDate, 'yyyy-MM') ===
          df.format(new Date(), 'yyyy-MM')
          ? calendarFocus
          : 'forwards'
        : calendarFocus,
    [calendarFocus, dateValue],
  );

  useEffect(() => {
    setDateValue(value || defaultValue);
  }, [value]);

  return (
    <>
      <Box
        sx={dateRangePicker}
        ref={anchorElRef}
        className={classNames(
          'date-range-selection',
          {
            open: isDatePickerOpen,
          },
          [className],
        )}
      >
        <TextFieldUi
          className='range-input start-date'
          label={!hidePlaceholder ? startDatePlaceholder : ''}
          value={
            dateValue?.[0]?.startDate
              ? formatDate(new Date(dateValue?.[0]?.startDate))
              : ''
          }
          onClick={onOpenDateRangePicker}
          disabled={isDatePickerOpen}
        />
        <KeyboardArrowRightRounded className='arrow-to' />
        <TextFieldUi
          className='range-input end-date'
          label={!hidePlaceholder ? endDatePlaceholder : ''}
          value={
            formatDate(dateValue?.[0]?.endDate) === formatDate(new Date(1))
              ? ''
              : formatDate(new Date(dateValue?.[0]?.endDate))
          }
          onClick={onOpenDateRangePicker}
          disabled={isDatePickerOpen}
        />
        <Grow
          in={formatDate(dateValue?.[0]?.endDate) !== formatDate(new Date(1))}
        >
          <Box>
            <ButtonUi
              isIconBtn
              className='reset'
              size='small'
              onClickHandler={onResetRangePicker}
            >
              <CloseRounded />
            </ButtonUi>
          </Box>
        </Grow>
        <ButtonUi
          isIconBtn
          className='date-select'
          size='small'
          onClickHandler={onOpenDateRangePicker}
        >
          <CalendarIcon />
        </ButtonUi>
      </Box>
      <Popover
        sx={pickerPopover}
        id='date-range-popover'
        open={isDatePickerOpen}
        anchorEl={anchorElRef.current}
        onClose={onCloseDateRangePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <DateRange
          onChange={onSelectRange}
          direction={direction}
          editableDateInputs={true}
          moveRangeOnFirstSelection={false}
          ranges={dateValue}
          locale={localeText}
          months={months}
          maxDate={maxDate}
          preventSnapRefocus={preventSnapRefocus}
          calendarFocus={calendarFocusWay}
          showSelectionPreview
          rangeColors={['#2392BA']}
          staticRanges={[]}
          inputRanges={[]}
          dayContentRenderer={customDayContent}
          navigatorRenderer={renderMonthAndYear}
        />
        <Box className='date-range-footer'>
          <Box className='drf-time-options'>
            <Typography className='drf-options-title' variant='caption'>
              Last:
            </Typography>
            {definedRanges.map((range) => {
              const { name, ...rangeValue } = range;

              const startDateEqual = dateValue?.[0]?.startDate
                ? formatDate(dateValue?.[0]?.startDate) ===
                  formatDate(rangeValue?.startDate)
                : false;
              const endDateEqual =
                formatDate(dateValue?.[0]?.endDate) ===
                formatDate(rangeValue?.endDate);
              const isActive = startDateEqual && endDateEqual;
              return (
                <ButtonUi
                  key={name}
                  className={classNames(
                    'time-range-btn',
                    { active: isActive },
                    [name],
                  )}
                  variant='outlined'
                  type='secondary'
                  size='small'
                  onClickHandler={() =>
                    onSelectRange({ selection: rangeValue })
                  }
                >
                  {name}
                </ButtonUi>
              );
            })}
          </Box>
          <Box className='drf-cancel-save'>
            <ButtonUi
              className='time-range-btn cancel'
              variant='text'
              size='small'
              onClickHandler={onCloseDateRangePicker}
            >
              {t('buttons.cancel')}
            </ButtonUi>
            <ButtonUi
              className='time-range-btn save'
              variant='contained'
              type='primary'
              size='small'
              onClickHandler={onSaveDateRange}
            >
              {t('buttons.save')}
            </ButtonUi>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
